import { FormSelect } from 'eone-ui/forms/form-select';
import { Select } from 'eone-ui/select/select';
import { isNil } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ENCRYPTED_GLOBAL_PLACEHOLDER } from '../constants';

// Important Note: Select from eone-ui requires props.Options to be non-nullable,
// so here we need to ensure options is compatible,
// cuz "undefined as props.options" has been widely used now
const stableEmptyArray = [];
Object.freeze(stableEmptyArray);

export interface SCSelectOption {
  /** value.  '-' divider. '---group name---' group name */
  value: string | number;
  label?: React.ReactNode;
  /** FontAwesome IconProp or AppFont icon name. Rendered before the label. */
  icon?: any;
  /** Set to "af" for AppFont icons. Omit for FontAwesome. */
  iconType?: string;
}
export type SCSelectOptions = SCSelectOption[] | undefined;

interface SCSelectCommonProps {
  disabled?: boolean;
  options?: SCSelectOptions;
  search?: boolean;
  onSearch?: (value: string) => void;
  loading?: boolean;
  placeholder?: string;
  secure?: boolean;
  className?: string;
  // props for eone-ui/forms/form-select
  useEoneFormSelect?: boolean;
  Label?: string;
  ShowRequiredIndicator?: boolean;
  RequiredStringIndicator?: string;
  useEoneLabelRender?: boolean;
  LabelRender?: (o: { value: any; originLabel: any }) => React.ReactNode;
  DataTestId?: string;
}

interface SCSelectSingleProps<T extends string | number> extends SCSelectCommonProps {
  multiple?: false;
  value?: T;
  onChange?: (value?: T) => void | Promise<void>;
}

interface SCSelectMultipleProps<T extends string | number> extends SCSelectCommonProps {
  multiple: true;
  value?: T[];
  onChange?: (value?: T[]) => void | Promise<void>;
}

export type SCSelectProps<T extends string | number> =
  SCSelectSingleProps<T> | SCSelectMultipleProps<T>;

export const SCSelect = <V extends string | number>(props: SCSelectProps<V>) => {
  const {
    onChange,
    multiple,
    options,
    secure,
    LabelRender,
    useEoneLabelRender,
    useEoneFormSelect,
    ...rest
  } = props;
  const OriginalSelect = useEoneFormSelect ? FormSelect : Select;

  const refCache = useRef<Record<string, SCSelectOption>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-focus the search input when the dropdown opens.
  // The dropdown panel is portaled to document.body, so we observe body
  // for the panel appearing and focus its search input.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      // Only act if focus is still within our select container (i.e. dropdown just opened)
      if (!container.contains(document.activeElement)) return;

      const searchInput = document.querySelector<HTMLInputElement>(
        '.eone-ui-dropdown-panel.open .eone-ui-select-menu-search-container input',
      );
      if (searchInput) {
        searchInput.focus();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const { newOptions, optionMap } = useMemo(() => {
    const optionMap = {};
    const newOptions =
      options?.map((option) => {
        optionMap[option.value] = option;

        return {
          Key: option.value,
          Value: option.label ?? option.value,
          Icon: option.icon,
          IconType: option.iconType,
        };
      }) ?? stableEmptyArray;

    return {
      newOptions,
      optionMap,
    };
  }, [options]);

  const handleChange = useCallback(
    async (value?: V | V[]) => {
      if (value !== null && value !== undefined) {
        if (!multiple) {
          refCache.current[value as any] = optionMap[value as any];
        }
      }
      if (isNil(value)) return;
      try {
        if (multiple) {
          await onChange?.(Array.isArray(value) ? value : [value]);
        } else {
          await onChange?.(value as V);
        }
      } catch {
        // ignore
      }
    },
    [onChange, multiple, optionMap],
  );

  const handleLabelRender = useCallback(
    ({ value, originLabel }: { value: V | V[]; originLabel: React.ReactNode }) => {
      if (secure) {
        // SC-1646: one mask constant, shared with the server.
        return ENCRYPTED_GLOBAL_PLACEHOLDER;
      }
      if (LabelRender && useEoneLabelRender) {
        return LabelRender({ value, originLabel });
      }
      let label = originLabel;

      if (value !== null && value !== undefined) {
        if (!multiple) {
          const singleValue = value as any;
          if (!optionMap[singleValue] && refCache.current[singleValue]) {
            label = refCache.current[singleValue]?.label || singleValue;
          }
        }
      }

      return label;
    },
    [optionMap, multiple, secure, LabelRender, useEoneLabelRender],
  );

  return (
    <div ref={containerRef}>
      <OriginalSelect
        {...rest}
        MultiSelect={props.multiple}
        Value={props.value}
        OnChange={handleChange}
        Options={newOptions}
        Search={props.search}
        OnChangeSearch={props.onSearch}
        Placeholder={props.placeholder}
        ContainerClass={props.className}
        Loading={props.loading}
        Disabled={props.disabled} // for eone-ui/select
        ReadOnly={props.disabled} // for eone-ui/forms/form-select
        LabelRender={handleLabelRender}
      />
    </div>
  );
};
